import { Table } from "antd"
import type { ColumnsType } from "antd/es/table"
import { RiseOutlined, FallOutlined } from "@ant-design/icons"
import './index.scss'
import type { StockFinancialData } from "@/types/stockPool"
import { filterFormatYiNumber } from "@/utils/filters"
import OpsFinancials from "@/components/OpsFinancials"

interface BalanceOverviewRow {
    id: number
    label: string
    value: number | string
    desc: string
}

const columns: ColumnsType<StockFinancialData> = [
    {
        title: '报告日期',
        dataIndex: 'report_date',
        key: 'report_date',
        render: (text: string) => <span style={{color: '#fff'}}>{text}</span>
    },
    {
        title: '营收(亿)',
        dataIndex: 'revenue',
        key: 'revenue',
        render: (text: string) => <span>{filterFormatYiNumber(text)}</span>
    },
    {
        title: '净利润(亿)',
        dataIndex: 'net_profit_parent',
        key: 'net_profit_parent',
        render: (text: number) => <span style={{color: `${text > 0 ? 'var(--green)' : 'var(--red)'}`}}>{filterFormatYiNumber(text)}</span>
    },
    {
        title: '毛利率%',
        dataIndex: 'gross_margin_pct',
        key: 'gross_margin_pct',
        render: (text: number) => <span>{text}%</span>
    },
    {
        title: '净利率%',
        dataIndex: 'net_margin_pct',
        key: 'net_margin_pct',
        render: (text: number) => <span>{text}%</span>
    }
]

const columns1: ColumnsType<BalanceOverviewRow> = [
    {
        title: '指标',
        dataIndex: 'label',
        key: 'label',
        render: (text: string) => <span style={{color: '#fff'}}>{text}</span>
    },
    {
        title: '数值',
        dataIndex: 'value',
        key: 'value',
    },
    {
        title: '说明',
        dataIndex: 'desc',
        key: 'desc',
    },
]
export default function FinancialData({data, stockCode}: {data?: StockFinancialData[], stockCode?: string}) {
    const financialData = data?.length ? data : []
    const tableData1: BalanceOverviewRow[] = [
        {
            id: 1,
            label: '总资产',
            value: data?.length ? filterFormatYiNumber(data[0]?.total_assets) + '亿' : 0,
            desc: `同比增长--%`,
        },
        {
            id: 2,
            label: '总负债',
            value: data?.length ? filterFormatYiNumber(data[0]?.total_liabilities) + '亿' : 0,
            desc: `资产负债率 ${data?.length ? data[0]?.debt_to_asset_ratio_pct : 0}%`,
        },
        {
            id: 3,
            label: '股东权益',
            value: data?.length ? filterFormatYiNumber(data[0]?.net_assets_equity) + '亿' : 0,
            desc: `同比增长--%`,
        },
        {
            id: 4,
            label: '应收账款',
            value: '--',
            desc: '--',
        },
        {
            id: 5,
            label: '存货',
            value: '--',
            desc: '--',
        },
    ]
    return (
        <div className="financial-data">
            <div className="financial-data__body">
                <div className="financial-data__title">核心指标（最近一季）</div>
                <div className="financial-data__items">
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">营收</div>
                        <div className="financial-data__item-value">{data?.length ? filterFormatYiNumber(data[0]?.revenue) : 0}<i className="unit text-10">亿</i></div>
                        <div className={`financial-data__item-percent ${data?.length && data[0]?.operating_revenue_yoy_pct && data[0]?.operating_revenue_yoy_pct > 0 ? 'red' : 'green' }`}>
                            {data?.length && data[0]?.operating_revenue_yoy_pct && data[0]?.operating_revenue_yoy_pct > 0 ?<RiseOutlined /> : <FallOutlined />} {data?.length && data[0]?.operating_revenue_yoy_pct ? data[0]?.operating_revenue_yoy_pct : '--'}% YoY
                        </div>
                    </div>
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">扣非净利润</div>
                        <div className="financial-data__item-value">{data?.length ? filterFormatYiNumber(data[0]?.net_profit_ex_non_recurring) : 0}<i className="unit text-10">亿</i></div>
                        <div className={`financial-data__item-percent ${data?.length && data[0]?.net_profit_ex_non_recurring_yoy_pct && data[0]?.net_profit_ex_non_recurring_yoy_pct > 0 ? 'red' : 'green' }`}>
                            {data?.length && data[0]?.net_profit_ex_non_recurring_yoy_pct && data[0]?.net_profit_ex_non_recurring_yoy_pct > 0 ? <RiseOutlined /> : <FallOutlined />} {data?.length && data[0]?.net_profit_ex_non_recurring_yoy_pct ? data[0]?.net_profit_ex_non_recurring_yoy_pct : '--'}% YoY
                        </div>
                    </div>
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">毛利率</div>
                        <div className="financial-data__item-value">{data?.length ? data[0]?.gross_margin_pct : 0}<i className="unit text-10">%</i></div>
                        <div className="financial-data__item-percent red">
                            <RiseOutlined />--% YoY
                        </div>
                    </div>
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">总资产</div>
                        <div className="financial-data__item-value">{data?.length ? filterFormatYiNumber(data[0]?.total_assets) : 0}<i className="unit text-10">亿</i></div>
                        <div className="financial-data__item-percent">
                            负债率 {data?.length ? data[0]?.debt_to_asset_ratio_pct : 0}%
                        </div>
                    </div>
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">货币资金</div>
                        <div className="financial-data__item-value">{data?.length ? filterFormatYiNumber(data[0]?.cash_and_equivalents) : 0}<i className="unit text-10">亿</i></div>
                        <div className="financial-data__item-percent">
                            流动比率 {data?.length ? data[0]?.current_ratio : 0}
                        </div>
                    </div>
                    <div className="financial-data__item">
                        <div className="financial-data__item-label">净利率</div>
                        <div className="financial-data__item-value">{data?.length ? data[0]?.net_margin_pct : 0}<i className="unit text-10">%</i></div>
                        <div className="financial-data__item-percent">
                            行业均值 --%
                        </div>
                    </div>
                </div>
                <div className="financial-data__title">历史利润表</div>
                <Table
                    columns={columns}
                    dataSource={financialData}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    className="financial-data__table"
                />
                <div className="financial-data__title">资产负债概况</div>
                <Table
                    columns={columns1}
                    dataSource={tableData1}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    className="financial-data__table"
                />
                {stockCode && <OpsFinancials code={stockCode} />}
            </div>
        </div>
    )
}